import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api, formatPrice } from '@/lib/api';
import { useCart } from '@/lib/CartContext';
import SEOHead from '@/components/SEOHead';
import ProductCard from '@/components/ProductCard';
import { ShoppingCart, Minus, Plus, ArrowLeft, Truck, ShieldCheck, Package } from 'lucide-react';
import { toast } from 'sonner';

export default function ProductDetailPage() {
    const { slug } = useParams();
    const [product, setProduct] = useState(null);
    const [related, setRelated] = useState([]);
    const [quantity, setQuantity] = useState(1);
    const [loading, setLoading] = useState(true);
    const { addItem } = useCart();

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const res = await api.get(`/products/${slug}`);
                setProduct(res.data);
                if (res.data.category_id) {
                    const relRes = await api.get(`/products?category=${res.data.category_id}&limit=4`);
                    setRelated((relRes.data.products || []).filter(p => p.slug !== slug));
                }
            } catch (e) { console.error(e); }
            setLoading(false);
        };
        load();
        setQuantity(1);
        window.scrollTo(0, 0);
    }, [slug]);

    const handleAdd = () => {
        if (!product) return;
        addItem(product, quantity);
        toast.success(`${product.name} x${quantity} agregado al carrito`);
    };

    if (loading) {
        return (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="grid lg:grid-cols-2 gap-10">
                    <div className="aspect-square rounded-2xl skeleton" />
                    <div className="space-y-4">
                        <div className="h-8 w-3/4 skeleton rounded-lg" />
                        <div className="h-6 w-1/3 skeleton rounded-lg" />
                        <div className="h-24 skeleton rounded-lg" />
                    </div>
                </div>
            </div>
        );
    }

    if (!product) {
        return (
            <div className="max-w-7xl mx-auto px-4 py-20 text-center">
                <p className="text-cp-text-muted text-lg mb-4">Producto no encontrado</p>
                <Link to="/productos" className="text-cp-secondary font-semibold">Volver a productos</Link>
            </div>
        );
    }

    const hasDiscount = product.compare_price && product.compare_price > product.price;

    return (
        <>
            <SEOHead
                title={product.name}
                description={product.description}
                image={product.images?.[0]}
                url={`https://cloudpet.com.ar/productos/${product.slug}`}
                type="product"
            />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12" data-testid="product-detail-page">
                {/* Breadcrumb */}
                <nav className="flex items-center gap-2 text-sm text-cp-text-muted mb-8" data-testid="breadcrumb">
                    <Link to="/" className="hover:text-cp-secondary">Inicio</Link>
                    <span>/</span>
                    <Link to="/productos" className="hover:text-cp-secondary">Productos</Link>
                    <span>/</span>
                    <span className="text-cp-text-main font-medium truncate">{product.name}</span>
                </nav>

                <div className="grid lg:grid-cols-2 gap-8 lg:gap-14">
                    {/* Image */}
                    <div className="aspect-square bg-cp-surface-soft rounded-3xl overflow-hidden">
                        {product.images?.[0] ? (
                            <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover" />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center text-cp-text-muted">Sin imagen</div>
                        )}
                    </div>

                    {/* Info */}
                    <div className="flex flex-col">
                        {hasDiscount && (
                            <span className="inline-flex self-start bg-cp-error/10 text-cp-error text-xs font-bold px-3 py-1 rounded-full mb-3">
                                {Math.round((1 - product.price / product.compare_price) * 100)}% OFF
                            </span>
                        )}
                        <h1 className="text-2xl sm:text-3xl font-heading font-bold text-cp-text-main mb-4" data-testid="product-title">
                            {product.name}
                        </h1>
                        <div className="flex items-baseline gap-3 mb-6">
                            <span className="text-3xl font-bold text-cp-brand" data-testid="product-price">
                                {formatPrice(product.price)}
                            </span>
                            {hasDiscount && (
                                <span className="text-lg text-cp-text-muted line-through">
                                    {formatPrice(product.compare_price)}
                                </span>
                            )}
                        </div>
                        <p className="text-cp-text-body leading-relaxed mb-8" data-testid="product-description">
                            {product.description}
                        </p>

                        {/* Quantity + Add */}
                        <div className="flex items-center gap-4 mb-8">
                            <div className="flex items-center border border-cp-border rounded-xl overflow-hidden">
                                <button
                                    data-testid="qty-minus"
                                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                    className="px-3 py-2.5 text-cp-text-body hover:bg-cp-bg-alt transition-colors"
                                >
                                    <Minus size={16} />
                                </button>
                                <span className="px-4 py-2.5 text-cp-text-main font-semibold text-sm min-w-[40px] text-center" data-testid="qty-value">
                                    {quantity}
                                </span>
                                <button
                                    data-testid="qty-plus"
                                    onClick={() => setQuantity(quantity + 1)}
                                    className="px-3 py-2.5 text-cp-text-body hover:bg-cp-bg-alt transition-colors"
                                >
                                    <Plus size={16} />
                                </button>
                            </div>
                            <button
                                data-testid="add-to-cart-btn"
                                onClick={handleAdd}
                                className="flex-1 flex items-center justify-center gap-2 bg-cp-secondary hover:bg-cp-secondary-hover text-white py-3 rounded-xl font-semibold transition-all active:scale-95"
                            >
                                <ShoppingCart size={18} />
                                Agregar al Carrito
                            </button>
                        </div>

                        {/* Badges */}
                        <div className="grid grid-cols-3 gap-3">
                            {[
                                { icon: Truck, text: 'Envio rapido' },
                                { icon: ShieldCheck, text: 'Calidad garantizada' },
                                { icon: Package, text: product.stock > 0 ? 'En stock' : 'Sin stock' },
                            ].map((b, i) => (
                                <div key={i} className="flex flex-col items-center gap-1.5 p-3 bg-cp-bg-alt rounded-xl text-center">
                                    <b.icon size={18} className="text-cp-primary" />
                                    <span className="text-xs text-cp-text-muted">{b.text}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Related */}
                {related.length > 0 && (
                    <section className="mt-16 lg:mt-20" data-testid="related-products">
                        <h2 className="text-xl font-heading font-bold text-cp-text-main mb-6">Productos Relacionados</h2>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
                            {related.slice(0, 4).map(p => <ProductCard key={p.id} product={p} />)}
                        </div>
                    </section>
                )}

                {/* Schema.org */}
                <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify([
                    {
                        "@context": "https://schema.org",
                        "@type": "BreadcrumbList",
                        "itemListElement": [
                            { "@type": "ListItem", "position": 1, "name": "Inicio", "item": "https://cloudpet.com.ar" },
                            { "@type": "ListItem", "position": 2, "name": "Productos", "item": "https://cloudpet.com.ar/productos" },
                            { "@type": "ListItem", "position": 3, "name": product.name, "item": `https://cloudpet.com.ar/productos/${product.slug}` }
                        ]
                    },
                    {
                        "@context": "https://schema.org",
                        "@type": "Product",
                        "name": product.name,
                        "description": product.description,
                        "image": product.images?.length > 0 ? product.images : [product.images?.[0] || ''],
                        "sku": product.sku || String(product.id),
                        "brand": { "@type": "Brand", "name": "CloudPet" },
                        "offers": {
                            "@type": "Offer",
                            "price": product.price,
                            "priceCurrency": "ARS",
                            "availability": product.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
                            "url": `https://cloudpet.com.ar/productos/${product.slug}`,
                            "priceValidUntil": new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0],
                            "seller": { "@type": "Organization", "name": "CloudPet" }
                        }
                    }
                ])}} />
            </div>
        </>
    );
}
